import workerAPI from '../api/workerAPI';
import { useEffect, useState, useRef } from 'react';
import {
  FaEdit,
  FaTrash,
  FaUserCircle,
  FaPlus,
  FaCamera,
  FaEye,
  FaEyeSlash,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import {
  MdEmail,
  MdWork,
  MdLock,
  MdBusiness,
  MdGroups,
  MdAccountCircle,
  MdLocationOn,
} from 'react-icons/md';
import AdminNavbar from './AdminNavbar';
import FaceRegistration from './FaceRegistration';

// Leaflet imports — make sure you have these in package.json:
// "leaflet": "^1.9.x"  and  import 'leaflet/dist/leaflet.css' in your main entry
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default icon paths broken by bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Roles available in the dropdown ──────────────────────────────────────────
const ROLES = ['Office', 'TL', 'Developer', 'Marketing', 'HR & Design', 'Project Leads','Designer'];

// Roles that don't need a team name
const NO_TEAM_ROLES = ['Office', 'Marketing'];

// All roles now support face registration
const NO_FACE_ROLES = [];

// ── Small component: clicking the map updates the pin position ────────────────
const LocationPicker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
};

// ── Default form state ────────────────────────────────────────────────────────
const defaultForm = {
  username: '',
  email: '',
  mobile: '',
  company: '',
  team: '',
  role: '',
  password: '',
  profileImage: null,
  preview: null,
  faceDescriptor: null,
  workMode: '',
  homeLocation: null, // { lat, lng, address }
};

const AddWorkers = () => {
  const [workers, setWorkers]                   = useState([]);
  const [showModal, setShowModal]               = useState(false);
  const [showPassword, setShowPassword]         = useState(false);
  const [isEditing, setIsEditing]               = useState(false);
  const [editingId, setEditingId]               = useState(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFaceModal, setShowFaceModal]       = useState(false);
  const [mapPin, setMapPin]                     = useState(null); // [lat, lng]
  const [geoLoading, setGeoLoading]             = useState(false);

  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchWorkers(); }, []);

  // ── When mapPin changes, do a reverse-geocode to get a human address ────────
  useEffect(() => {
    if (!mapPin) return;
    (async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${mapPin[0]}&lon=${mapPin[1]}&format=json`
        );
        const data = await res.json();
        setFormData(prev => ({
          ...prev,
          homeLocation: {
            lat: mapPin[0],
            lng: mapPin[1],
            address: data.display_name || `${mapPin[0].toFixed(5)}, ${mapPin[1].toFixed(5)}`,
          },
        }));
      } catch {
        setFormData(prev => ({
          ...prev,
          homeLocation: {
            lat: mapPin[0],
            lng: mapPin[1],
            address: `${mapPin[0].toFixed(5)}, ${mapPin[1].toFixed(5)}`,
          },
        }));
      }
    })();
  }, [mapPin]);

  const fetchWorkers = async () => {
    try {
      const res = await workerAPI.get('/');
      setWorkers(res.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

const handleImageChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    setFormData(prev => ({
      ...prev,
      profileImage: reader.result, // base64 string
      preview: reader.result,
    }));
  };
  reader.readAsDataURL(file);
};
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Clear homeLocation when switching away from Remote
      if (name === 'workMode' && value !== 'Remote') {
        updated.homeLocation = null;
        setMapPin(null);
      }
      return updated;
    });
  };

  // ── Use browser geolocation to jump map to worker's current position ────────
  const handleDetectLocation = () => {
    if (!navigator.geolocation) return alert('Geolocation not supported by your browser.');
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapPin([pos.coords.latitude, pos.coords.longitude]);
        setGeoLoading(false);
      },
      () => {
        alert('Unable to retrieve location. Please pin it manually on the map.');
        setGeoLoading(false);
      }
    );
  };

  const openAddModal = () => {
    setFormData(defaultForm);
    setMapPin(null);
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };

 const openEditModal = (worker) => {
  const loc = worker.homeLocation;
  const pin = loc?.lat ? [loc.lat, loc.lng] : null;
  setMapPin(pin);
  setFormData({
    username:       worker.username,
    email:          worker.email,
    mobile:         worker.mobile,
    company:        worker.company,
    team:           worker.team,
    role:           worker.role,
    password:       worker.password,
    profileImage:   null,        // only set if user picks a new image
    preview:        worker.profileImage, // Cloudinary URL — shows existing photo
    faceDescriptor: null,
    workMode:       worker.workMode || '',
    homeLocation:   loc || null,
  });
  setEditingId(worker._id);
  setIsEditing(true);
  setShowModal(true);
};
  const handleSubmit = async (e) => {
  e.preventDefault();

  // Face required for non-exempt roles on add
  if (!isEditing && !NO_FACE_ROLES.includes(formData.role) && !formData.faceDescriptor) {
    alert('❗ Please register the face before saving the worker.');
    return;
  }

  // Home location required for Remote workers
  if (formData.workMode === 'Remote' && !formData.homeLocation) {
    alert('❗ Please pin the worker\'s home location on the map.');
    return;
  }

  // Build plain JSON payload instead of FormData
  const payload = {
    username:  formData.username,
    email:     formData.email,
    mobile:    formData.mobile,
    team:      formData.team,
    role:      formData.role,
    password:  formData.password,
    workMode:  formData.workMode,
  };

  if (formData.homeLocation) {
    payload.homeLocation = JSON.stringify(formData.homeLocation);
  }

  // profileImage is already base64 from handleImageChange
  if (formData.profileImage) {
    payload.profileImage = formData.profileImage;
  }

  if (formData.faceDescriptor) {
    payload.faceDescriptor = JSON.stringify(formData.faceDescriptor);
  }

  try {
    if (isEditing) {
      await workerAPI.put(`/${editingId}`, payload);
      alert('✅ Worker updated successfully!');
    } else {
      await workerAPI.post('/add', payload);
      alert('✅ Worker saved successfully!');
    }

    fetchWorkers();
    setFormData(defaultForm);
    setMapPin(null);
    setIsEditing(false);
    setEditingId(null);
    setShowModal(false);
    setShowPassword(false);
  } catch (error) {
    console.error('Error saving worker:', error);
    alert('❌ Failed to save worker.');
  }
};
  const handleDelete = async () => {
    try {
      await workerAPI.delete(`/${selectedWorkerId}`);
      fetchWorkers();
      setShowDeleteConfirm(false);
      setSelectedWorkerId(null);
    } catch (error) {
      console.error('Error deleting worker:', error);
    }
  };

  // Default map center — India
  const defaultCenter = [20.5937, 78.9629];
  const mapCenter = mapPin || defaultCenter;

  return (
    <div>
      <AdminNavbar />
      <div className="min-h-screen pt-24 px-6 bg-gradient-to-br from-purple-100 via-white to-pink-100">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex justify-between items-center max-w-7xl mx-auto mb-6">
          <h2 className="text-3xl font-bold text-purple-700 font-mono">Add Workers</h2>
          <button
            onClick={openAddModal}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center gap-2"
          >
            <FaPlus /> Add New Worker
          </button>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="overflow-x-auto max-w-7xl mx-auto bg-white shadow-md rounded-lg">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-purple-100 text-purple-800 sticky top-0 z-10">
                <tr>
                  {['Profile','Username','Email','Mobile Number','Company Name','Role',
                    'Team Name','Work Mode','Home Location','Password','Actions'].map(h => (
                    <th key={h} className="px-3 py-5 text-left text-l font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workers.map((worker, index) => (
                  <tr key={index} className="hover:bg-purple-50">
                    <td className="px-3 py-4">
                      <img
                        src={worker.profileImage}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover border border-purple-400"
                      />
                    </td>
                    <td className="px-3 py-4 font-semibold text-purple-700">{worker.username}</td>
                    <td className="px-3 py-4">{worker.email}</td>
                    <td className="px-3 py-4">{worker.mobile}</td>
                    <td className="px-3 py-4">{worker.company}</td>
                    <td className="px-3 py-4">{worker.role}</td>
                    <td className="px-3 py-4">{worker.team}</td>
                    <td className="px-3 py-4">{worker.workMode}</td>
                    <td className="px-3 py-4 max-w-[180px]">
                      {worker.homeLocation?.address ? (
                        <span
                          className="flex items-start gap-1 text-xs text-gray-600"
                          title={worker.homeLocation.address}
                        >
                          <FaMapMarkerAlt className="text-purple-500 mt-0.5 shrink-0" />
                          <span className="line-clamp-2">{worker.homeLocation.address}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-4">{worker.password}</td>
                    <td className="px-3 py-4 flex gap-4">
                      <button
                        onClick={() => openEditModal(worker)}
                        className="text-blue-500 hover:text-blue-700 text-lg pt-3"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => { setSelectedWorkerId(worker._id); setShowDeleteConfirm(true); }}
                        className="text-red-500 hover:text-red-700 text-lg pt-3"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
                {workers.length === 0 && (
                  <tr>
                    <td colSpan="11" className="text-center py-6 text-gray-500">
                      No workers added yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Delete confirm ──────────────────────────────────────────── */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg p-6 w-80 shadow-lg text-center">
                <h2 className="text-lg font-semibold mb-4 text-gray-700">
                  Are you sure you want to delete this worker?
                </h2>
                <div className="flex justify-center gap-4">
                  <button onClick={handleDelete} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">Yes</button>
                  <button onClick={() => { setShowDeleteConfirm(false); setSelectedWorkerId(null); }} className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 overflow-y-auto py-8">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-xl relative my-auto">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-4 text-gray-600 hover:text-red-500 text-xl font-bold"
              >×</button>

              <h3 className="text-2xl font-bold text-purple-700 mb-4 text-center">
                {isEditing ? 'Edit Worker' : 'Add New Worker'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Profile image picker */}
                <div className="relative flex justify-center mb-4">
                  <div className="relative w-24 h-24">
                    {formData.preview ? (
                      <img src={formData.preview} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2 border-purple-500" />
                    ) : (
                      <FaUserCircle className="w-24 h-24 text-gray-400 rounded-full border-2 border-dashed border-purple-300" />
                    )}
                    <label className="absolute bottom-0 right-0 bg-purple-600 p-1 rounded-full cursor-pointer hover:bg-purple-700">
                      <FaCamera className="text-white" />
                      <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Username */}
                  <div className="relative">
                    <MdAccountCircle className="absolute left-3 top-4 text-gray-400" />
                    <input type="text" name="username" value={formData.username} onChange={handleChange} required
                      className="peer pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400" />
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Username</label>
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <MdEmail className="absolute left-3 top-4 text-gray-400" />
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required
                      className="peer pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400" />
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Email</label>
                  </div>

                  {/* Mobile */}
                  <div className="relative">
                    <FaUserCircle className="absolute left-3 top-4 text-gray-400" />
                    <input type="text" name="mobile" value={formData.mobile} onChange={handleChange} required
                      className="peer pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400" />
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Mobile Number</label>
                  </div>

                  {/* Role — updated with new roles */}
                  <div className="relative">
                    <MdWork className="absolute left-3 top-4 text-gray-400" />
                    <select name="role" value={formData.role} onChange={handleChange} required
                      className="pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400">
                      <option value="" disabled hidden></option>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Role</label>
                  </div>

                  {/* Team Name — hidden for Office & Marketing */}
                  {!NO_TEAM_ROLES.includes(formData.role) && formData.role && (
                    <div className="relative">
                      <MdGroups className="absolute left-3 top-4 text-gray-400" />
                      <input type="text" name="team" value={formData.team} onChange={handleChange}
                        className="peer pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400" />
                      <label className="absolute left-10 top-2 text-gray-500 text-sm">Team Name</label>
                    </div>
                  )}

                  {/* Password */}
                  <div className="relative">
                    <MdLock className="absolute left-3 top-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password" value={formData.password} onChange={handleChange} required
                      className="peer pl-10 pt-5 pb-1 pr-10 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Password</label>
                    <div className="absolute right-3 top-4 text-gray-500 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>

                  {/* Work Mode */}
                  <div className="relative">
                    <MdBusiness className="absolute left-3 top-4 text-gray-400" />
                    <select name="workMode" value={formData.workMode} onChange={handleChange} required
                      className="pl-10 pt-5 pb-1 pr-3 border rounded w-full focus:outline-none focus:ring-2 focus:ring-purple-400">
                      <option value="" disabled hidden></option>
                      <option value="On-site">On-site</option>
                      <option value="Remote">Remote</option>
                    </select>
                    <label className="absolute left-10 top-2 text-gray-500 text-sm">Work Mode</label>
                  </div>

                </div>

                {/* ── Home Location Map (Remote only) ──────────────────────── */}
                {formData.workMode === 'Remote' && (
                  <div className="border border-purple-300 rounded-lg p-4 bg-purple-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                        <MdLocationOn className="text-lg" /> Worker Home Location
                      </h4>
                      <button
                        type="button"
                        onClick={handleDetectLocation}
                        disabled={geoLoading}
                        className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {geoLoading ? 'Detecting…' : '📍 Use My Location'}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500">
                      Click anywhere on the map to drop a pin at the worker's home location, or use the button above to auto-detect.
                    </p>

                    {/* Leaflet Map */}
                    <div className="rounded-lg overflow-hidden border border-purple-200" style={{ height: 260 }}>
                      <MapContainer
                        center={mapCenter}
                        zoom={mapPin ? 13 : 5}
                        style={{ height: '100%', width: '100%' }}
                        key={mapPin ? `${mapPin[0]}-${mapPin[1]}` : 'default'}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <LocationPicker position={mapPin} setPosition={setMapPin} />
                      </MapContainer>
                    </div>

                    {/* Address display */}
                    {formData.homeLocation?.address && (
                      <div className="flex items-start gap-2 bg-white border border-purple-200 rounded p-2 text-sm text-gray-700">
                        <FaMapMarkerAlt className="text-purple-500 mt-0.5 shrink-0" />
                        <span>{formData.homeLocation.address}</span>
                      </div>
                    )}

                    {!mapPin && (
                      <p className="text-xs text-amber-600 font-medium">
                        ⚠️ A home location pin is required for Remote workers.
                      </p>
                    )}
                  </div>
                )}

                {/* Face Register button */}
                {!NO_FACE_ROLES.includes(formData.role) && formData.role && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowFaceModal(true)}
                      className="mt-2 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 flex items-center gap-2 mx-auto"
                    >
                      📸 Register Face
                      {formData.faceDescriptor && (
                        <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">✓ Done</span>
                      )}
                    </button>
                  </div>
                )}

                {/* Submit */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-purple-600 text-white px-6 sm:px-11 lg:px-20 py-2 rounded hover:bg-purple-700 w-full sm:w-auto"
                  >
                    {isEditing ? 'Update Worker' : 'Save Worker'}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

        {/* ── Face Registration Modal ──────────────────────────────────────── */}
        {showFaceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-3xl relative">
              <button onClick={() => setShowFaceModal(false)} className="absolute top-3 right-4 text-gray-600 hover:text-red-500 text-xl font-bold">×</button>
              <FaceRegistration
                onSuccess={(descriptor) => {
                  setFormData(prev => ({ ...prev, faceDescriptor: descriptor }));
                  setShowFaceModal(false);
                }}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AddWorkers;